/**
 * DevicesTab — Lists devices assigned to the resident with assign/remove controls.
 *
 * Mirrors the Area Profile DevicesTab pattern:
 *   - Full table layout with Device Name, Description, Model, Serial Number, Status columns
 *   - Three-tier status: green (active), yellow (active but stale), grey (inactive),
 *     with manufacturers that never heartbeat exempt from the stale tier
 *   - Device links route to V2 community device pages by manufacture_id
 *
 * Actions: Assign Device button, per-row remove icon
 */

import StatusPopover from '../../../../../components/v2/StatusPopover.jsx';
import { Link } from 'react-router-dom';
import { skipsStaleCheck, showsMuted, MUTED_BADGE } from '../../../../../utils/deviceStatus.js';
import { getDeviceProfileLink } from '../../../../../utils/deviceLinks.js';

const STALE_THRESHOLD_MS = 30 * 3600000; // 30 hours

/* Manufacturers on this surface whose devices never heartbeat: last_active never
   advances, so the stale test would permanently show them yellow and tell staff to
   go test working hardware. They report Active whenever they are assigned. */
const STALE_EXEMPT_MFG = [6, 7];

const DevicesTab = ({
  resDeviceData,
  residentName,
  devEditRights,
  onAssign,
  onRemove,
  /* Device ids with an active alarm, campus-wide (activeAlertDeviceIds). No longer blocks
     Remove (2026-10-16); kept so the tab can mark alarming rows again if wanted. */
  alertedDeviceIds,
}) => {

  /** Derive status class and label from is_active + last_active */
  const getStatus = (device) => {
    if (!Number(device.is_active)) {
      return { className: 'dp-status--inactive', label: 'Inactive', title: undefined };
    }
    /* Manufacturers that never heartbeat can't be judged on last_active — see
       STALE_EXEMPT_MFG above. Skipping the test rather than faking a timestamp
       keeps the reason visible at the point the badge is chosen. */
    /* Suppression outranks staleness: it is a deliberate state someone set and can
       undo, while staleness is inferred from a timestamp. */
    if (showsMuted(device.is_active, device.away_until)) return MUTED_BADGE;
    const stale = !skipsStaleCheck(device.manufacture_id, STALE_EXEMPT_MFG)
      && (device.last_active
        ? (new Date() - new Date(device.last_active)) > STALE_THRESHOLD_MS
        : true);
    if (stale) {
      return {
        className: 'dp-status--warning',
        label: 'Active',
        title: 'Device may be stale — test the device to ensure functionality',
      };
    }
    return { className: 'dp-status--active', label: 'Active', title: undefined };
  };

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">
          Assigned Devices
          <span className="pp-section__count">({resDeviceData.length})</span>
        </h3>
        {devEditRights ? (
          <div className="pp-section__actions">
            <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={onAssign}>
              + Assign Device
            </button>
          </div>
        ) : null}
      </div>
      <div className="pp-section__body pp-section__body--flush">
        <div className="dp-table-wrap">
          <table className="dp-table">
            <thead className="dp-thead">
              <tr>
                <th className="dp-th">Device Name</th>
                <th className="dp-th">Description</th>
                <th className="dp-th">Model</th>
                <th className="dp-th">Serial Number</th>
                <th className="dp-th dp-th--center">Status</th>
                {devEditRights ? <th className="dp-th dp-th--center" style={{ width: '80px' }}>Remove</th> : null}
              </tr>
            </thead>
            <tbody>
              {resDeviceData.map((device) => {
                const status = getStatus(device);
                /* Device-profile rule (utils/deviceLinks.js). The old strict
                   [1,4,6].includes() missed string ids, so nothing linked when the
                   proc returned manufacture_id as a string. */
                const deviceLink = getDeviceProfileLink(device);
                return (
                  <tr key={device.device_id} className="dp-tr">
                    <td className="dp-td">
                      {deviceLink
                          ? <Link to={deviceLink} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                              {device.device_name || '—'}
                            </Link>
                          : device.device_name || '—'
                      }
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{device.descriptions || '—'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{device.model_name || device.manufacture_model || '—'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text dp-cell-text--mono">{device.manufacture_uuid || '—'}</span>
                    </td>
                    <td className="dp-td dp-td--center">
                      <StatusPopover message={status.title}>
                        <span className={`dp-status ${status.className}`}>
                          <span className="dp-status__dot" aria-hidden="true" />
                          {status.label}
                        </span>
                      </StatusPopover>
                    </td>
                    {devEditRights ? (
                      <td className="dp-td dp-td--center">
                        {(() => {
                          /* Remove stays available while the device alarms (it was
                             blocked in 4.3.9): the page's remove confirmation shows
                             DeviceAlarmWarning instead, same as the area profile. */
                          return (
                            <button
                              className="pp-remove-btn"
                              onClick={() => onRemove(device)}
                              title={`Remove ${device.device_name || 'device'} from ${residentName}`}
                            >
                              ✕
                            </button>
                          );
                        })()}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              {resDeviceData.length === 0 ? (
                <tr className="dp-tr">
                  <td className="dp-td dp-td--center" colSpan={devEditRights ? 6 : 5}>
                    <span className="dp-cell-text dp-cell-text--muted">No devices assigned to this resident</span>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DevicesTab;
